import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";

interface Note {
  _id: Id<"notes">;
  _creationTime: number;
  title: string;
  content: string;
  color?: string;
  createdBy: Id<"users">;
  isPublic: boolean;
}

export default function NotesGrid() {
  const [showPublic, setShowPublic] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const myNotes = useQuery(api.notes.getMyNotes);
  const publicNotes = useQuery(api.notes.getPublicNotes);
  const createNote = useMutation(api.notes.createNote);
  const updateNote = useMutation(api.notes.updateNote);
  const deleteNote = useMutation(api.notes.deleteNote);

  const notes = showPublic ? publicNotes : myNotes;

  const colors = [
    "#fef3c7", // 黄色
    "#dbeafe", // 蓝色
    "#dcfce7", // 绿色
    "#fce7f3", // 粉色
    "#f3e8ff", // 紫色
    "#fed7d7", // 红色
  ];

  const handleCreateNote = () => {
    setSelectedNote({
      _id: "" as Id<"notes">,
      _creationTime: Date.now(),
      title: "",
      content: "",
      color: colors[0],
      createdBy: "" as Id<"users">,
      isPublic: false,
    });
    setIsCreating(true);
    setIsEditing(true);
  };

  const handleSaveNote = async (noteData: {
    title: string;
    content: string;
    color: string;
    isPublic: boolean;
  }) => {
    try {
      if (isCreating) {
        await createNote(noteData);
        setIsCreating(false);
      } else if (selectedNote) {
        await updateNote({
          noteId: selectedNote._id,
          ...noteData,
        });
      }
      setSelectedNote(null);
      setIsEditing(false);
    } catch (error) {
      console.error("保存便签失败:", error);
    }
  };

  const handleDeleteNote = async (noteId: Id<"notes">) => {
    if (confirm("确定要删除这个便签吗？")) {
      try {
        await deleteNote({ noteId });
        setSelectedNote(null);
        setIsEditing(false);
      } catch (error) {
        console.error("删除便签失败:", error);
      }
    }
  };

  return (
    <div className="p-3 sm:p-6">
      {/* 头部控制栏 */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <div className="flex items-center gap-3 sm:gap-4">
          <h2 className="text-xl sm:text-2xl font-bold">
            {showPublic ? "公共便签" : "我的便签"}
          </h2>
          <button
            onClick={() => setShowPublic(!showPublic)}
            className="flex items-center gap-2 px-3 py-1.5 sm:py-1 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
          >
            {showPublic ? <EyeOff size={14} /> : <Eye size={14} />}
            {showPublic ? "查看我的" : "查看公共"}
          </button>
        </div>
        
        {!showPublic && (
          <button
            onClick={handleCreateNote}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm sm:text-base w-full sm:w-auto justify-center sm:justify-start"
          >
            <Plus size={16} />
            新建便签
          </button>
        )}
      </div>

      {/* 瀑布流网格 - 移动端优化 */}
      <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-3 sm:gap-4 space-y-3 sm:space-y-4">
        {notes?.map((note) => (
          <div
            key={note._id}
            className="break-inside-avoid mb-3 sm:mb-4 p-3 sm:p-4 rounded-lg shadow-sm border cursor-pointer hover:shadow-md transition-shadow active:scale-95 sm:active:scale-100"
            style={{ backgroundColor: note.color || "#fef3c7" }}
            onClick={() => {
              setSelectedNote(note);
              setIsEditing(false);
              setIsCreating(false);
            }}
          >
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-semibold text-gray-800 line-clamp-2 text-sm sm:text-base">
                {note.title || "无标题"}
              </h3>
              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                {note.isPublic && (
                  <Eye size={12} className="text-gray-500" />
                )}
              </div>
            </div>
            <p className="text-gray-700 text-xs sm:text-sm line-clamp-6 whitespace-pre-wrap leading-relaxed">
              {note.content}
            </p>
            <div className="mt-2 text-xs text-gray-500">
              {new Date(note._creationTime).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>

      {/* 便签详情/编辑模态框 */}
      {selectedNote && (
        <NoteModal
          note={selectedNote}
          isEditing={isEditing}
          isCreating={isCreating}
          colors={colors}
          onSave={handleSaveNote}
          onEdit={() => setIsEditing(true)}
          onDelete={() => handleDeleteNote(selectedNote._id)}
          onClose={() => {
            setSelectedNote(null);
            setIsEditing(false);
            setIsCreating(false);
          }}
          canEdit={!showPublic} // 只有在查看自己的便签时才能编辑
        />
      )}
    </div>
  );
}

// 便签模态框组件
function NoteModal({
  note,
  isEditing,
  isCreating,
  colors,
  onSave,
  onEdit,
  onDelete,
  onClose,
  canEdit,
}: {
  note: Note;
  isEditing: boolean;
  isCreating: boolean;
  colors: string[];
  onSave: (data: { title: string; content: string; color: string; isPublic: boolean }) => void;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
  canEdit: boolean;
}) {
  const [title, setTitle] = useState(note.title);
  const [content, setContent] = useState(note.content);
  const [color, setColor] = useState(note.color || colors[0]);
  const [isPublic, setIsPublic] = useState(note.isPublic);

  const handleSave = () => {
    onSave({ title, content, color, isPublic });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* 头部 */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">
            {isCreating ? "新建便签" : isEditing ? "编辑便签" : "便签详情"}
          </h3>
          <div className="flex items-center gap-2">
            {!isEditing && canEdit && (
              <button
                onClick={onEdit}
                className="p-2 text-gray-500 hover:text-blue-500 transition-colors"
              >
                <Edit size={16} />
              </button>
            )}
            {!isEditing && canEdit && !isCreating && (
              <button
                onClick={onDelete}
                className="p-2 text-gray-500 hover:text-red-500 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* 内容 */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {isEditing ? (
            <div className="space-y-4">
              {/* 标题输入 */}
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="便签标题"
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {/* 内容输入 */}
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="写下你的想法..."
                rows={10}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />

              {/* 颜色选择 */}
              <div>
                <label className="block text-sm font-medium mb-2">选择颜色</label>
                <div className="flex gap-2">
                  {colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      className={`w-8 h-8 rounded-full border-2 ${
                        color === c ? "border-gray-800" : "border-gray-300"
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              {/* 公开设置 */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">公开便签（其他人可以看到）</span>
              </label>

              {/* 操作按钮 */}
              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleSave}
                  className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  保存
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  取消
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div
                className="p-4 rounded-lg"
                style={{ backgroundColor: note.color || "#fef3c7" }}
              >
                <h4 className="text-xl font-semibold mb-3 text-gray-800">
                  {note.title || "无标题"}
                </h4>
                <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {note.content}
                </div>
                <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-300">
                  <span className="text-sm text-gray-600">
                    {new Date(note._creationTime).toLocaleString()}
                  </span>
                  {note.isPublic && (
                    <span className="flex items-center gap-1 text-sm text-gray-600">
                      <Eye size={14} />
                      公开
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}